"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { tv, type VariantProps } from "../utils/tv";
import { recursiveCloneChildren } from "../utils/recursive-clone-children";
import type { PolymorphicComponentProps } from "../utils/polymorphic";

const BADGE_ROOT_NAME = "BadgeRoot";
const BADGE_ICON_NAME = "BadgeIcon";
const BADGE_DOT_NAME = "BadgeDot";

/**
 * AlignUI Badge Variants
 * Supports compound component pattern with Badge.Root, Badge.Icon, and Badge.Dot
 */
export const badgeVariants = tv({
  slots: {
    root: "inline-flex items-center justify-center whitespace-nowrap",
    icon: "flex shrink-0 items-center justify-center",
    dot: [
      "shrink-0 rounded-full",
      "before:block before:size-1.5 before:rounded-full before:bg-current",
    ],
  },
  variants: {
    variant: {
      filled: {},
      light: {},
      lighter: {},
      stroke: {
        root: "ring-1 ring-inset ring-current/25",
      },
    },
    color: {
      gray: {},
      primary: {},
      blue: {},
      orange: {},
      red: {},
      green: {},
      yellow: {},
    },
    size: {
      small: {
        root: "h-5 gap-1 rounded-4 px-1.5 text-subheading-2xs uppercase tracking-wider",
        icon: "size-3.5",
        dot: "size-3.5",
      },
      medium: {
        root: "h-6 gap-1.5 rounded-6 px-2 text-label-xs",
        icon: "size-4",
        dot: "size-4",
      },
    },
  },
  compoundVariants: [
    // Gray
    {
      color: "gray",
      variant: "filled",
      class: { root: "bg-gray-500 text-static-white" },
    },
    {
      color: "gray",
      variant: "light",
      class: { root: "bg-gray-200 text-gray-700" },
    },
    {
      color: "gray",
      variant: "lighter",
      class: { root: "bg-gray-100 text-gray-700" },
    },
    {
      color: "gray",
      variant: "stroke",
      class: { root: "text-gray-700" },
    },
    // Primary (Green)
    {
      color: "primary",
      variant: "filled",
      class: { root: "bg-primary-base text-static-white" },
    },
    {
      color: "primary",
      variant: "light",
      class: { root: "bg-primary-light text-primary-700" },
    },
    {
      color: "primary",
      variant: "lighter",
      class: { root: "bg-primary-lighter text-primary-700" },
    },
    {
      color: "primary",
      variant: "stroke",
      class: { root: "text-primary-base" },
    },
    // Green (Success)
    {
      color: "green",
      variant: "filled",
      class: { root: "bg-success-base text-static-white" },
    },
    {
      color: "green",
      variant: "light",
      class: { root: "bg-success-light text-green-700" },
    },
    {
      color: "green",
      variant: "lighter",
      class: { root: "bg-success-lighter text-green-700" },
    },
    {
      color: "green",
      variant: "stroke",
      class: { root: "text-success-base" },
    },
    // Red (Error)
    {
      color: "red",
      variant: "filled",
      class: { root: "bg-error-base text-static-white" },
    },
    {
      color: "red",
      variant: "light",
      class: { root: "bg-error-light text-red-700" },
    },
    {
      color: "red",
      variant: "lighter",
      class: { root: "bg-error-lighter text-red-700" },
    },
    {
      color: "red",
      variant: "stroke",
      class: { root: "text-error-base" },
    },
    // Yellow (Warning)
    {
      color: "yellow",
      variant: "filled",
      class: { root: "bg-warning-base text-yellow-950" },
    },
    {
      color: "yellow",
      variant: "light",
      class: { root: "bg-warning-light text-yellow-700" },
    },
    {
      color: "yellow",
      variant: "lighter",
      class: { root: "bg-warning-lighter text-yellow-700" },
    },
    {
      color: "yellow",
      variant: "stroke",
      class: { root: "text-warning-base" },
    },
    // Blue (Info)
    {
      color: "blue",
      variant: "filled",
      class: { root: "bg-info-base text-static-white" },
    },
    {
      color: "blue",
      variant: "light",
      class: { root: "bg-info-light text-blue-700" },
    },
    {
      color: "blue",
      variant: "lighter",
      class: { root: "bg-info-lighter text-blue-700" },
    },
    {
      color: "blue",
      variant: "stroke",
      class: { root: "text-info-base" },
    },
    // Orange
    {
      color: "orange",
      variant: "filled",
      class: { root: "bg-orange-500 text-static-white" },
    },
    {
      color: "orange",
      variant: "light",
      class: { root: "bg-orange-200 text-orange-700" },
    },
    {
      color: "orange",
      variant: "lighter",
      class: { root: "bg-orange-100 text-orange-700" },
    },
    {
      color: "orange",
      variant: "stroke",
      class: { root: "text-orange-600" },
    },
  ],
  defaultVariants: {
    variant: "filled",
    color: "gray",
    size: "medium",
  },
});

type BadgeSharedProps = VariantProps<typeof badgeVariants>;

type BadgeRootProps = VariantProps<typeof badgeVariants> &
  React.HTMLAttributes<HTMLSpanElement> & {
    asChild?: boolean;
  };

const BadgeRoot = React.forwardRef<HTMLSpanElement, BadgeRootProps>(
  ({ children, variant, color, size, asChild, className, ...rest }, forwardedRef) => {
    const uniqueId = React.useId();
    const Component = asChild ? Slot : "span";
    const { root } = badgeVariants({ variant, color, size });

    const sharedProps: BadgeSharedProps = { variant, color, size };

    const extendedChildren = recursiveCloneChildren(
      children as React.ReactElement[],
      sharedProps,
      [BADGE_ICON_NAME, BADGE_DOT_NAME],
      uniqueId,
      asChild
    );

    return (
      <Component ref={forwardedRef} className={root({ class: className })} {...rest}>
        {extendedChildren}
      </Component>
    );
  }
);
BadgeRoot.displayName = BADGE_ROOT_NAME;

function BadgeIcon<T extends React.ElementType>({
  className,
  as,
  variant,
  color,
  size,
  ...rest
}: PolymorphicComponentProps<T, BadgeSharedProps>) {
  const Component = as || "div";
  const { icon } = badgeVariants({ variant, color, size });

  return <Component className={icon({ class: className })} {...rest} />;
}
BadgeIcon.displayName = BADGE_ICON_NAME;

function BadgeDot({
  className,
  variant,
  color,
  size,
  ...rest
}: BadgeSharedProps & React.HTMLAttributes<HTMLSpanElement>) {
  const { dot } = badgeVariants({ variant, color, size });

  return <span className={dot({ class: className })} {...rest} />;
}
BadgeDot.displayName = BADGE_DOT_NAME;

export { BadgeRoot as Root, BadgeIcon as Icon, BadgeDot as Dot };
